import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import api from '../services/api';

const CondominioContext = createContext();

const rotuloTipo = {
  administrador: 'Administrador',
  sindico: 'Síndico',
  subsindico: 'Subsíndico',
  porteiro: 'Porteiro',
  morador: 'Morador',
  funcionario: 'Funcionário'
};

const prioridadeTipo = {
  administrador: 5,
  sindico: 4,
  subsindico: 3,
  porteiro: 2,
  funcionario: 1,
  morador: 0
};

const normalizarCondominio = (item) => {
  const condominio = item?.condominio || item || {};
  const condominioId = condominio.condominio_id || item?.condominio_id;
  if (!condominioId) return null;
  return {
    condominio_id: condominioId,
    condominio_nome: condominio.condominio_nome || item?.condominio_nome || 'Condomínio',
    condominio_cidade: condominio.condominio_cidade || item?.condominio_cidade || '',
    condominio_estado: condominio.condominio_estado || item?.condominio_estado || '',
    tipo_acesso: item?.tipo_acesso || condominio.tipo_acesso || null,
    apartamento_id: item?.apartamento_id || condominio.apartamento_id || null
  };
};

const mesclarCondominios = (lista) => {
  const map = new Map();
  lista.map(normalizarCondominio).filter(Boolean).forEach((item) => {
    const key = String(item.condominio_id);
    const atual = map.get(key);
    if (!atual || (prioridadeTipo[item.tipo_acesso] || 0) > (prioridadeTipo[atual.tipo_acesso] || 0)) {
      map.set(key, item);
    }
  });
  return Array.from(map.values());
};

export const useCondominio = () => {
  const context = useContext(CondominioContext);
  if (!context) {
    throw new Error('useCondominio deve ser usado dentro de um CondominioProvider');
  }
  return context;
};

export const CondominioProvider = ({ children }) => {
  const { user } = useAuth();
  const [condominioSelecionado, setCondominioSelecionado] = useState(() => {
    // Tentar carregar do localStorage ao iniciar
    try {
      const saved = localStorage.getItem('condominioSelecionado');
      return saved ? JSON.parse(saved) : null;
    } catch (error) {
      console.error('Erro ao carregar condomínio do localStorage:', error);
      return null;
    }
  });
  const [condominios, setCondominios] = useState([]);
  const [loading, setLoading] = useState(true);

  // Salvar no localStorage quando o condomínio mudar
  useEffect(() => {
    if (condominioSelecionado) {
      try {
        localStorage.setItem('condominioSelecionado', JSON.stringify(condominioSelecionado));
      } catch (error) {
        console.error('Erro ao salvar condomínio no localStorage:', error);
      }
    }
  }, [condominioSelecionado]);

  useEffect(() => {
    if (user) {
      loadCondominios();
    } else {
      // Limpar condomínio selecionado quando usuário deslogar
      setCondominioSelecionado(null);
      localStorage.removeItem('condominioSelecionado');
    }
  }, [user?.tipo, user?.id, user?.vinculos?.length]);

  const loadCondominios = async () => {
    try {
      setLoading(true);
      const isAdmin = user?.tipo === 'administrador';
      let condominiosData = [];

      if (isAdmin) {
        const response = await api.get('/condominios?limit=500');
        condominiosData = response.data?.data?.condominios || [];
      } else {
        const dosVinculos = user?.vinculos || [];
        let daApi = [];
        try {
          const response = await api.get('/usuarios/meus-condominios');
          daApi = response.data?.data?.condominios || [];
        } catch (error) {
          console.error('Erro ao carregar meus condomínios:', error);
        }
        condominiosData = mesclarCondominios([...daApi, ...dosVinculos]);
      }

      setCondominios(condominiosData);

      let condominioSalvo = null;
      try {
        const saved = localStorage.getItem('condominioSelecionado');
        if (saved) {
          condominioSalvo = JSON.parse(saved);
          const existe = condominiosData.find((c) => String(c.condominio_id) === String(condominioSalvo.condominio_id));
          if (!existe) {
            condominioSalvo = null;
            localStorage.removeItem('condominioSelecionado');
          } else {
            condominioSalvo = existe;
          }
        }
      } catch (error) {
        console.error('Erro ao verificar condomínio salvo:', error);
      }

      if (condominioSelecionado || condominioSalvo) {
        if (!condominioSelecionado && condominioSalvo) {
          setCondominioSelecionado(condominioSalvo);
        }
        return;
      }

      const preferido = condominiosData.find((c) => String(c.condominio_id) === String(user?.condominio_id));
      if (preferido || condominiosData[0]) {
        setCondominioSelecionado(preferido || condominiosData[0]);
      }
    } catch (error) {
      console.error('Erro ao carregar condomínios:', error);
    } finally {
      setLoading(false);
    }
  };

  const selecionarCondominio = (condominio) => {
    setCondominioSelecionado(condominio);
    if (condominio) {
      localStorage.setItem('condominioSelecionado', JSON.stringify(condominio));
    }
  };

  const getCondominioId = () => {
    if (condominioSelecionado?.condominio_id) return condominioSelecionado.condominio_id;
    if (user?.tipo === 'administrador') return null;
    return user?.condominio_id || null;
  };

  const getCondominioNome = () => {
    if (condominioSelecionado?.condominio_nome) return condominioSelecionado.condominio_nome;
    if (user?.tipo === 'administrador' || condominios.length > 1) return 'Selecione um condomínio';
    return 'Condomínio';
  };

  const isAdministrador = () => {
    return user?.tipo === 'administrador';
  };

  const isMorador = () => {
    return user?.tipo === 'morador';
  };

  const temMultiplosCondominios = () => {
    return condominios.length > 1;
  };

  const deveMostrarSeletor = () => {
    if (isAdministrador() && condominios.length >= 1) return true;
    return temMultiplosCondominios();
  };

  const value = {
    condominioSelecionado,
    condominios,
    loading,
    selecionarCondominio,
    getCondominioId,
    getCondominioNome,
    rotuloTipo,
    isAdministrador,
    isMorador,
    temMultiplosCondominios,
    deveMostrarSeletor,
    loadCondominios
  };

  return (
    <CondominioContext.Provider value={value}>
      {children}
    </CondominioContext.Provider>
  );
};
